import * as THREE from 'three';
import { Random } from '../../utils/Random.js';
const _position = new THREE.Vector3();
const _quaternion = new THREE.Quaternion();
const _scale = new THREE.Vector3();
const _matrix = new THREE.Matrix4();
const _normal = new THREE.Vector3();
const _tangentU = new THREE.Vector3();
const _tangentV = new THREE.Vector3();
const _center = new THREE.Vector3();
const _forward = new THREE.Vector3(0, 0, 1);

/**
 * Builds one InstancedMesh grid on a rectangular face (many glyphs, one draw call).
 */
export class BinaryInstancedSurface {
  /**
   * @param {{
   *   material: import('./BinaryMaterial.js').BinaryMaterial;
   *   width: number;
   *   height: number;
   *   cellsPerMeter: number;
   *   center: THREE.Vector3;
   *   normal: THREE.Vector3;
   *   tangentU: THREE.Vector3;
   *   tangentV: THREE.Vector3;
   *   seed?: number;
   *   cellIdOffset?: number;
   * }} options
   */
  constructor({
    material,
    width,
    height,
    cellsPerMeter,
    center,
    normal,
    tangentU,
    tangentV,
    seed = 1,
    cellIdOffset = 0,
  }) {
    this.material = material;
    this.width = width;
    this.height = height;
    this.rng = new Random(seed);

    const cols = Math.max(4, Math.round(width * cellsPerMeter));
    const rows = Math.max(4, Math.round(height * cellsPerMeter));
    this.columns = cols;
    this.rows = rows;
    this.instanceCount = cols * rows;

    const geometry = new THREE.PlaneGeometry(1, 1);
    const cellIds = new Float32Array(this.instanceCount);
    const phases = new Float32Array(this.instanceCount);
    const brightness = new Float32Array(this.instanceCount);

    this.mesh = new THREE.InstancedMesh(
      geometry,
      material.threeMaterial,
      this.instanceCount,
    );
    this.mesh.frustumCulled = true;
    this.mesh.name = 'BinaryInstancedSurface';

    _normal.copy(normal).normalize();
    _tangentU.copy(tangentU).normalize();
    _tangentV.copy(tangentV).normalize();

    const cellW = width / cols;
    const cellH = height / rows;
    const epsilon = 0.028;

    let index = 0;
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        const u = (col + 0.5) / cols - 0.5;
        const v = (row + 0.5) / rows - 0.5;

        _center
          .copy(center)
          .addScaledVector(_tangentU, u * width)
          .addScaledVector(_tangentV, v * height)
          .addScaledVector(_normal, epsilon);

        _position.copy(_center);
        _quaternion.setFromUnitVectors(_forward, _normal);
        _scale.set(cellW * material.settings.glyphAspect, cellH, 1);
        _matrix.compose(_position, _quaternion, _scale);
        this.mesh.setMatrixAt(index, _matrix);

        cellIds[index] = cellIdOffset + index;
        phases[index] = this.rng.next();
        brightness[index] = 0.58 + this.rng.next() * 0.42;
        index += 1;
      }
    }

    geometry.setAttribute(
      'aCellId',
      new THREE.InstancedBufferAttribute(cellIds, 1),
    );
    geometry.setAttribute(
      'aPhase',
      new THREE.InstancedBufferAttribute(phases, 1),
    );
    geometry.setAttribute(
      'aBrightness',
      new THREE.InstancedBufferAttribute(brightness, 1),
    );

    this.mesh.instanceMatrix.needsUpdate = true;
  }

  dispose() {
    this.mesh.geometry.dispose();
  }
}
